import { z } from "zod";

import { type CompanyData, DOCUMENT_TONE_OPTIONS, type ProfileData } from "./profile-schema";

/**
 * Per-type wizard copy + form schema for the AI document generator. NOTE: this
 * is no longer the source of truth for *which* tools appear on the dashboard —
 * that catalog now comes from the server (`GET /api/generate/catalog`, see
 * `document-catalog.tsx`) so it always matches admin Settings. `DOCUMENT_TYPES`
 * here only supplies the built-in stepper copy (intros, context labels, dates)
 * and validation; every built-in id must stay in sync with the backend catalog
 * (`backend/src/lib/document-catalog.ts`) — the `check:catalog` guard enforces
 * this. Generation itself is templated in `lib/document-generator.ts`.
 */

export type DocumentCategory =
  | "hiring"
  | "disciplinary"
  | "compensation"
  | "leave"
  | "offboarding"
  | "policies";

export const DOCUMENT_CATEGORIES: {
  id: DocumentCategory;
  label: string;
  description: string;
}[] = [
  {
    id: "hiring",
    label: "Hiring & Onboarding",
    description: "Bring new people on board.",
  },
  {
    id: "disciplinary",
    label: "Disciplinary & Performance",
    description: "Address conduct and performance.",
  },
  {
    id: "compensation",
    label: "Compensation & Promotion",
    description: "Pay, raises, and advancement.",
  },
  {
    id: "leave",
    label: "Leave & Verification",
    description: "Time off and confirmations.",
  },
  {
    id: "offboarding",
    label: "Offboarding",
    description: "Endings and final paperwork.",
  },
  {
    id: "policies",
    label: "Policies & Handbooks",
    description: "Company-wide policies and multi-page documents.",
  },
];

export type DocumentTypeId =
  | "offer"
  | "contract"
  | "probation-confirmation"
  | "onboarding-welcome"
  | "warning"
  | "final-warning"
  | "pip"
  | "suspension"
  | "salary-increment"
  | "promotion"
  | "bonus"
  | "leave-approval"
  | "employment-verification"
  | "termination"
  | "resignation-acceptance"
  | "reference"
  | "final-settlement"
  | "job-description"
  | "employee-handbook"
  | "nda";

export type DocumentTypeDef = {
  id: DocumentTypeId;
  name: string;
  category: DocumentCategory;
  /** One-line description shown on the catalog card. */
  blurb: string;
  /** Phrase the generic builder slots after "We are writing to …". */
  intro?: string;
  /** Label for the free-text context field; defaults to "Details". */
  contextLabel?: string;
  /** Placeholder guiding what to write in the context field. */
  contextPlaceholder?: string;
  /** Label for the date field; defaults to "Effective date". */
  dateLabel?: string;
};

export const DOCUMENT_TYPES: readonly DocumentTypeDef[] = [
  // Hiring & Onboarding
  {
    id: "offer",
    name: "Offer Letter",
    category: "hiring",
    blurb: "Extend a job offer with role, start date, and terms.",
    contextLabel: "Offer details",
    contextPlaceholder:
      "Role, salary, key benefits, reporting line, and any conditions of the offer.",
    dateLabel: "Proposed start date",
  },
  {
    id: "contract",
    name: "Employment Contract",
    category: "hiring",
    blurb: "Set out the terms of an employment agreement.",
    contextLabel: "Key terms",
    contextPlaceholder: "Position, compensation, hours, probation, notice period, and other terms.",
  },
  {
    id: "probation-confirmation",
    name: "Probation Confirmation",
    category: "hiring",
    blurb: "Confirm successful completion of probation.",
    intro: "confirm the successful completion of your probation period",
    contextLabel: "Notes",
    contextPlaceholder: "Outcome of the probation review and confirmation of permanent terms.",
  },
  {
    id: "onboarding-welcome",
    name: "Onboarding Welcome",
    category: "hiring",
    blurb: "Welcome a new hire and outline first steps.",
    intro: "warmly welcome you to the team",
    contextLabel: "Welcome details",
    contextPlaceholder: "First-day logistics, who they'll meet, and what to bring or expect.",
  },
  {
    id: "job-description",
    name: "Job Description",
    category: "hiring",
    blurb: "Describe a role's responsibilities, requirements, and reporting line.",
    contextLabel: "Role details",
    contextPlaceholder:
      "Key responsibilities, required skills and experience, reporting line, and any other expectations.",
  },

  // Disciplinary & Performance
  {
    id: "warning",
    name: "Warning Letter",
    category: "disciplinary",
    blurb: "Document a disciplinary issue and set expectations.",
    contextLabel: "What happened?",
    contextPlaceholder:
      "Describe the incident or conduct, when it occurred, and any prior discussions.",
    dateLabel: "Date of issue",
  },
  {
    id: "final-warning",
    name: "Final Warning",
    category: "disciplinary",
    blurb: "Issue a final written warning before further action.",
    intro: "issue this final written warning regarding",
    contextLabel: "What happened?",
    contextPlaceholder:
      "The continued or serious conduct, prior warnings given, and what must change.",
    dateLabel: "Date of issue",
  },
  {
    id: "pip",
    name: "Performance Improvement Plan",
    category: "disciplinary",
    blurb: "Set measurable goals to improve performance.",
    intro: "set out a performance improvement plan addressing",
    contextLabel: "Goals & concerns",
    contextPlaceholder:
      "Performance gaps, specific measurable goals, support offered, and review dates.",
  },
  {
    id: "suspension",
    name: "Suspension Letter",
    category: "disciplinary",
    blurb: "Confirm suspension from duty pending review.",
    intro: "confirm your suspension from duty in relation to",
    contextLabel: "Circumstances",
    contextPlaceholder:
      "Reason for suspension, whether it is paid, and the expected review process.",
    dateLabel: "Suspension start date",
  },

  // Compensation & Promotion
  {
    id: "salary-increment",
    name: "Salary Increment",
    category: "compensation",
    blurb: "Confirm a pay increase.",
    intro: "confirm an adjustment to your salary",
    contextLabel: "Increment details",
    contextPlaceholder:
      "New salary, amount/percentage of increase, and the reason for the adjustment.",
  },
  {
    id: "promotion",
    name: "Promotion Letter",
    category: "compensation",
    blurb: "Confirm a promotion to a new role.",
    intro: "confirm your promotion",
    contextLabel: "Promotion details",
    contextPlaceholder:
      "New title, responsibilities, reporting line, and any change in compensation.",
  },
  {
    id: "bonus",
    name: "Bonus Letter",
    category: "compensation",
    blurb: "Award a bonus and explain its basis.",
    intro: "confirm the award of a bonus",
    contextLabel: "Bonus details",
    contextPlaceholder:
      "Bonus amount, the period or achievement it recognises, and payment timing.",
  },

  // Leave & Verification
  {
    id: "leave-approval",
    name: "Leave Approval",
    category: "leave",
    blurb: "Approve a leave request.",
    intro: "confirm the approval of your leave request",
    contextLabel: "Leave details",
    contextPlaceholder: "Type of leave, dates, and any handover or cover arrangements.",
    dateLabel: "Leave start date",
  },
  {
    id: "employment-verification",
    name: "Employment Verification",
    category: "leave",
    blurb: "Confirm employment details to a third party.",
    intro: "confirm the employment details of the above-named individual",
    contextLabel: "What to confirm",
    contextPlaceholder:
      "Position, dates of employment, and any other details requested by the recipient.",
  },

  // Offboarding
  {
    id: "termination",
    name: "Termination Letter",
    category: "offboarding",
    blurb: "Communicate the end of employment and next steps.",
    contextLabel: "Reason & circumstances",
    contextPlaceholder:
      "Reason for termination, final working day, and any handover or final-pay notes.",
  },
  {
    id: "resignation-acceptance",
    name: "Resignation Acceptance",
    category: "offboarding",
    blurb: "Acknowledge and accept a resignation.",
    intro: "acknowledge and accept your resignation",
    contextLabel: "Details",
    contextPlaceholder: "Last working day, handover expectations, and any final arrangements.",
    dateLabel: "Last working day",
  },
  {
    id: "reference",
    name: "Reference Letter",
    category: "offboarding",
    blurb: "Provide a reference for a former employee.",
    intro: "provide this reference confirming",
    contextLabel: "Reference details",
    contextPlaceholder:
      "Role held, period of employment, strengths, and anything you wish to highlight.",
  },
  {
    id: "final-settlement",
    name: "Final Settlement",
    category: "offboarding",
    blurb: "Set out final pay and settlement on exit.",
    intro: "set out your final settlement on the conclusion of employment",
    contextLabel: "Settlement details",
    contextPlaceholder: "Final salary, accrued leave, deductions, and any other amounts owed.",
  },
  // Policies & Handbooks
  {
    id: "employee-handbook",
    name: "Employee Handbook",
    category: "policies",
    blurb: "Build a multi-page handbook of company policies, conduct, and terms.",
  },
  {
    id: "nda",
    name: "Non-Disclosure Agreement",
    category: "policies",
    blurb: "Bind the parties to keep confidential information secret and define its permitted use.",
    contextLabel: "Agreement details",
    contextPlaceholder:
      "What confidential information is covered, permitted use, duration, and the parties involved.",
  },
];

export { DOCUMENT_TONE_OPTIONS };

const TONE_VALUES = DOCUMENT_TONE_OPTIONS.map((t) => t.value) as [string, ...string[]];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The flat form behind the generator wizard. Steps render and validate subsets
 * of this (see `STEP_FIELDS`). The document type is fixed by the route, so it is
 * not edited in the form's steps — only carried through for generation.
 */
/**
 * Reserved RHF field holding the linked employee's *record id*. Deliberately
 * dunder-prefixed so it can never collide with a tool's own placeholder key —
 * several tools declare an `employeeId` placeholder for the human "Staff Number"
 * field, which is a different value and lives under its own (`employeeId`) key.
 */
export const EMPLOYEE_LINK_FIELD = "__employeeLink";

export const documentFormSchema = z.object({
  // Built-in catalog id *or* an admin-created custom tool slug. Fixed by the
  // route (never edited in the form), so a plain string is enough here.
  documentType: z.string().min(1),
  /** Linked employee record id; "" when entering the recipient manually. */
  [EMPLOYEE_LINK_FIELD]: z.string(),
  recipientName: z.string().trim().min(1, "Recipient name is required").max(160),
  recipientJobTitle: z.string().trim().max(160),
  recipientDepartment: z.string().trim().max(160),
  context: z.string().trim().min(1, "Add the details for this document").max(4000),
  effectiveDate: z.string().trim().min(1, "A date is required").regex(ISO_DATE, "Use a valid date"),
  tone: z.enum(TONE_VALUES, { message: "Choose a tone" }),
  signatoryName: z.string().trim().max(160),
  signatoryTitle: z.string().trim().max(160),
  companyName: z.string().trim().max(200),
});

export type DocumentFormValues = z.infer<typeof documentFormSchema>;

/** The fields each wizard step is responsible for (drives per-step validation). */
export const STEP_FIELDS = {
  recipient: ["recipientName", "recipientJobTitle", "recipientDepartment"],
  details: ["context", "effectiveDate", "tone"],
} as const satisfies Record<string, readonly (keyof DocumentFormValues)[]>;

/** Type guard for validating a route param against the known document types. */
export function isDocumentTypeId(value: string): value is DocumentTypeId {
  return DOCUMENT_TYPES.some((t) => t.id === value);
}

/**
 * A lookup of the type definition. Accepts any id (built-in or a custom tool
 * slug); unknown ids fall back to the first type so callers always get a def —
 * the display name for custom tools is supplied separately (the generator passes
 * the resolved tool label).
 */
export function documentTypeDef(id: string): DocumentTypeDef {
  // DOCUMENT_TYPES is a non-empty literal, so the fallback always resolves.
  return DOCUMENT_TYPES.find((t) => t.id === id) ?? (DOCUMENT_TYPES[0] as DocumentTypeDef);
}

/** A generic def for an admin-created custom tool not in the static catalog. */
export function genericDocumentTypeDef(id: string, name: string, blurb: string): DocumentTypeDef {
  return { id: id as DocumentTypeId, name, category: "hiring", blurb };
}

/** The document types in a given category, in catalog order. */
export function typesInCategory(category: DocumentCategory): DocumentTypeDef[] {
  return DOCUMENT_TYPES.filter((t) => t.category === category);
}

/**
 * A recipient prefill for the generator, derived from an employee profile so a
 * "Generate for this employee" deep link opens the wizard already linked to that
 * person (the link the saved document carries via `employeeId`).
 */
export type DocumentRecipientSeed = {
  employeeId: string;
  name: string;
  jobTitle: string;
  department: string;
};

/**
 * Builds the form defaults for a chosen document type, seeding company + tone
 * from the workspace company. Signatory defaults remain available to seed the
 * editor's first signer, but are not collected in tool input steps. When
 * `recipient` is given (an employee deep link), the recipient fields and
 * `employeeId` are prefilled too, so the document is linked to that profile
 * from the start.
 */
export function buildDocumentDefaults(opts: {
  documentType: string;
  company: CompanyData | null;
  profile: ProfileData | null;
  recipient?: DocumentRecipientSeed | null;
}): DocumentFormValues {
  const { documentType, company, profile, recipient } = opts;
  const today = new Date().toISOString().slice(0, 10);
  return {
    documentType,
    [EMPLOYEE_LINK_FIELD]: recipient?.employeeId ?? "",
    recipientName: recipient?.name ?? "",
    recipientJobTitle: recipient?.jobTitle ?? "",
    recipientDepartment: recipient?.department ?? "",
    context: "",
    effectiveDate: today,
    tone: company?.documentTone || DOCUMENT_TONE_OPTIONS[0].value,
    signatoryName: company?.issuerName || profile?.displayName || "",
    signatoryTitle: company?.issuerTitle || profile?.jobTitle || "",
    companyName: company?.companyName || "",
  };
}
